import { Component, Inject, OnInit } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertsType } from '@rappi/ui/alerts';
import { Subscription } from 'rxjs';

import { AlertsService } from '../../../../services/alerts/alerts.service';
import { MakersFinancesMgrService } from '../../../services/makers-finances-mgr.service';
import { PHONE_FIELD_ERROR, PHONE_FIELD_MESSAGE } from '../../phone-field/phone-field.constants';
import { SelectOption } from '../../purchase-order-detail/sections/section-base.types';
import { FH_COMPANY_LABEL } from '../../company-form/company-form.constants';
import {
  CONTACT_DUPLICATE_CHECK_PAGE,
  CONTACT_DUPLICATE_CHECK_PAGE_SIZE,
  CONTACT_DUPLICATE_CHECK_MAX_PAGES,
  CONTACT_FORM_DIALOG_LABEL,
} from './contact-form-dialog.constants';

export interface ContactFormDialogData {
  /** Present in edit mode — opens the dialog pre-populated with the contact's current data. */
  contactId?: string;
  /** Present in create mode only. Unused by the create endpoint but kept for signature compat. */
  companyId?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-contact-form-dialog',
  templateUrl: './contact-form-dialog.component.html',
  styleUrls: ['./contact-form-dialog.component.scss'],
})
export class ContactFormDialogComponent implements OnInit {
  readonly LABELS = CONTACT_FORM_DIALOG_LABEL;
  readonly COMMON = FH_COMPANY_LABEL;
  readonly PHONE_ERROR = PHONE_FIELD_ERROR;
  readonly PHONE_MESSAGE = PHONE_FIELD_MESSAGE;

  private readonly _contactId: number | null = null;
  // INI-13919-79: the in-flight duplicate-lookup subscription, so it can be torn down on close.
  private _lookupSub: Subscription | null = null;

  name = '';
  email = '';
  phone = '';
  jobTitle = '';
  submitted = false;
  isSaving = false;
  isLoading = false;
  loadFailed = false;
  // INI-13919-79: set when the duplicate pre-check finds the email already registered.
  duplicateEmail = false;

  constructor(
    public readonly dialogRef: MatDialogRef<ContactFormDialogComponent, SelectOption | null>,
    private readonly financesService: MakersFinancesMgrService,
    private readonly alerts: AlertsService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ContactFormDialogData,
  ) {
    if (data.contactId) {
      this._contactId = Number(data.contactId);
    }
    // INI-13919-79: beforeClosed fires for Cancel, the X button, backdrop and Escape alike.
    // Kill the pending duplicate lookup so a late response can't start a POST after the
    // caller has already received null.
    this.dialogRef.beforeClosed().subscribe(() => this._lookupSub?.unsubscribe());
  }

  get isEditMode(): boolean {
    return this._contactId !== null;
  }

  get title(): string {
    return this.isEditMode ? this.LABELS.EDIT_TITLE : this.LABELS.CREATE_TITLE;
  }

  get confirmLabel(): string {
    return this.isEditMode ? this.LABELS.SAVE : this.LABELS.CREATE;
  }

  ngOnInit(): void {
    const id = this._contactId;
    if (id === null) {
      return;
    }
    this.isLoading = true;
    this.financesService.getContact(id).subscribe({
      next: res => {
        this.isLoading = false;
        this.name = res.data.full_name ?? '';
        this.email = res.data.email ?? '';
        this.phone = res.data.phone ?? '';
        this.jobTitle = res.data.job_title ?? '';
      },
      error: () => {
        this.isLoading = false;
        this.loadFailed = true;
        this.alerts.openAlerts(this.LABELS.LOAD_FAILED, AlertsType.error);
      },
    });
  }

  get hasNameError(): boolean {
    return this.submitted && !this.name.trim();
  }

  get hasEmailError(): boolean {
    return this.submitted && !EMAIL_PATTERN.test(this.email.trim());
  }

  // INI-13919-79: only meaningful once the email format itself is valid.
  get hasDuplicateEmailError(): boolean {
    return this.submitted && this.duplicateEmail && !this.hasEmailError;
  }

  // INI-13919-79: wired to the email input's (ngModelChange) — the duplicate flag must not
  // outlive the address it was checked against, or a fresh address shows as already registered.
  onEmailChange(): void {
    this.duplicateEmail = false;
  }

  phoneErrorMessage(errors: ValidationErrors | null): string | null {
    if (!errors || !errors[this.PHONE_ERROR.LENGTH]) {
      return null;
    }
    return this.PHONE_MESSAGE[this.PHONE_ERROR.LENGTH];
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    this.submitted = true;
    this.duplicateEmail = false;
    if (this.hasNameError || this.hasEmailError || this.isSaving || this.loadFailed) {
      return;
    }

    this.isSaving = true;

    const id = this._contactId;
    if (id !== null) {
      this._updateContact(id);
      return;
    }

    // INI-13919-79: best-effort duplicate pre-check against the BBR-native contact directory
    // (GET /contacts) before POST /contacts. The backend does not enforce email uniqueness yet;
    // if the lookup itself fails, fall through and let the backend stay the source of truth.
    this._checkEmailNotInUse(this.email.trim(), CONTACT_DUPLICATE_CHECK_PAGE);
  }

  // INI-13919-79: scans GET /contacts page by page — `q` matches names as well as emails, so an
  // exact email match can sit past the first page. Stops on match, exhaustion, or the page cap;
  // any lookup failure falls through to creation (backend remains the source of truth).
  private _checkEmailNotInUse(email: string, page: number): void {
    this._lookupSub = this.financesService
      .listContacts(page, CONTACT_DUPLICATE_CHECK_PAGE_SIZE, email)
      .subscribe({
        next: res => {
          const records = res.data?.records ?? [];
          const alreadyRegistered = records.some(
            record => (record.email ?? '').trim().toLowerCase() === email.toLowerCase(),
          );
          if (alreadyRegistered) {
            this.isSaving = false;
            this.duplicateEmail = true;
            return;
          }
          const pagination = res.data?.pagination;
          const exhausted = !pagination
            || records.length < CONTACT_DUPLICATE_CHECK_PAGE_SIZE
            || page >= pagination.total_page
            || page >= CONTACT_DUPLICATE_CHECK_MAX_PAGES;
          if (exhausted) {
            this._createContact(email);
            return;
          }
          this._checkEmailNotInUse(email, page + 1);
        },
        error: () => this._createContact(email),
      });
  }

  // INI-13919-79: takes the email captured at submit time — the inputs stay editable while the
  // lookup is pending, so reading this.email here could POST an address that was never checked.
  private _createContact(email: string): void {
    this.financesService.createContact({
      full_name: this.name.trim(),
      email,
      phone: this.phone.trim() || undefined,
      job_title: this.jobTitle.trim() || undefined,
    }).subscribe({
      next: res => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.CREATED, AlertsType.success);
        this.dialogRef.close({
          id: String(res.data.id),
          name: `${res.data.full_name}  ·  ${res.data.email}`,
        });
      },
      error: () => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.CREATE_FAILED, AlertsType.error);
      },
    });
  }

  private _updateContact(id: number): void {
    this.financesService.updateContact(id, {
      email: this.email.trim(),
      full_name: this.name.trim() || undefined,
      phone: this.phone.trim() || undefined,
      job_title: this.jobTitle.trim() || undefined,
    }).subscribe({
      next: res => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.SAVED, AlertsType.success);
        this.dialogRef.close({
          id: String(res.data.id),
          name: `${res.data.full_name}  ·  ${res.data.email}`,
        });
      },
      error: () => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.SAVE_FAILED, AlertsType.error);
      },
    });
  }
}
